import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clapperboard, ArrowRight, Upload, FileText, Film, Store, Users, ShoppingBag,
  History, Coins, Download, Sparkles, Check, Plus, Minus, Menu, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicFooter } from "@/components/PublicFooter";
import { getPackages } from "@/lib/api";
import { formatRupiah } from "@/lib/site";

const AVAILABLE = [
  { icon: ShoppingBag, title: "Video Jualan AI", desc: "Buat video promosi dari foto produk Anda." },
  { icon: Upload, title: "Upload Foto Produk", desc: "Mulai dari foto produk yang Anda punya." },
  { icon: Sparkles, title: "Konsep & Script AI", desc: "AI membantu menyusun konsep dan naskah." },
  { icon: History, title: "Riwayat Video", desc: "Semua video tersimpan dalam satu tempat." },
  { icon: Coins, title: "Sistem Kredit", desc: "Pakai kredit untuk setiap pembuatan video." },
  { icon: Download, title: "Preview & Download", desc: "Lihat dan unduh hasil video jika selesai." },
];

const COMING_SOON = ["Payment", "Subscription", "Meta Ads", "TikTok Ads", "Content Calendar", "Analytics", "AI Caption"];

const AUDIENCE = [
  { icon: Store, title: "UMKM" },
  { icon: ShoppingBag, title: "Seller Marketplace" },
  { icon: Store, title: "Pemilik Toko Online" },
  { icon: Sparkles, title: "Pemilik Brand" },
  { icon: Users, title: "Affiliate Marketer" },
  { icon: Users, title: "Digital Marketer" },
];

const BENEFITS = [
  "Lebih mudah membuat materi promosi.",
  "Lebih cepat memulai pembuatan video.",
  "Tidak harus mulai dari nol untuk membuat script.",
  "Semua video tersimpan dalam satu tempat.",
];

const FAQS = [
  { q: "Apa itu Mas Tofa AI?", a: "Mas Tofa AI adalah alat bantu berbasis AI untuk membantu Anda membuat konten pemasaran lebih mudah. Produk utama saat ini adalah Video Jualan AI, yang membantu membuat video promosi dari foto produk." },
  { q: "Apakah harus bisa editing video?", a: "Tidak. Anda cukup mengunggah foto produk dan mengisi informasi produk, lalu AI membantu menyiapkan konsep dan videonya." },
  { q: "Apa yang perlu disiapkan?", a: "Siapkan foto produk Anda dan informasi produk seperti deskripsi, keunggulan, dan hal yang ingin ditonjolkan." },
  { q: "Berapa lama proses video?", a: "Durasi proses dapat bervariasi tergantung antrean dan penyedia pemrosesan. Anda dapat memantau status video di dashboard." },
  { q: "Apakah bisa menggunakan foto produk sendiri?", a: "Ya. Anda menggunakan foto produk Anda sendiri sebagai bahan utama pembuatan video." },
  { q: "Apa itu kredit?", a: "Kredit digunakan untuk setiap pembuatan video. Saldo kredit Anda dapat dilihat di dashboard." },
  { q: "Apakah video bisa di-download?", a: "Jika proses video selesai, Anda dapat melihat preview dan mengunduh hasilnya." },
  { q: "Apakah Mas Tofa AI gratis?", a: "Anda bisa mendaftar dan mulai mencoba. Penggunaan pembuatan video memakai sistem kredit." },
];

const STEPS = [
  { n: "01", icon: Upload, title: "Upload Foto Produk", desc: "Unggah foto produk yang ingin Anda promosikan." },
  { n: "02", icon: FileText, title: "Masukkan Informasi Produk", desc: "Isi deskripsi dan detail penting produk Anda." },
  { n: "03", icon: Film, title: "AI Membuat Video Jualan", desc: "AI membantu menyiapkan konsep dan video promosi." },
];

function PublicNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
            <Clapperboard className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <p className="font-heading font-bold tracking-tight text-slate-900">Mas Tofa AI</p>
            <p className="text-[11px] text-slate-500">AI untuk Video Jualan</p>
          </div>
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          <a href="#cara-kerja" className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900">Cara Kerja</a>
          <a href="#fitur" className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900">Fitur</a>
          <a href="#harga" className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900">Harga</a>
          <a href="#faq" className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-900">FAQ</a>
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/masuk" data-testid="nav-login">
            <Button variant="ghost" className="text-slate-700 hover:bg-slate-100">Masuk</Button>
          </Link>
          <Link to="/daftar" data-testid="nav-register">
            <Button className="rounded-full bg-blue-600 px-5 text-white hover:bg-blue-700 active:scale-95 transition-transform">Mulai Gratis</Button>
          </Link>
        </div>
        <button className="md:hidden text-slate-700" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-5 py-4 md:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-3">
            <a href="#cara-kerja" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600">Cara Kerja</a>
            <a href="#fitur" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600">Fitur</a>
            <a href="#harga" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600">Harga</a>
            <a href="#faq" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-600">FAQ</a>
            <Link to="/masuk" data-testid="nav-login-mobile"><Button variant="outline" className="w-full">Masuk</Button></Link>
            <Link to="/daftar" data-testid="nav-register-mobile"><Button className="w-full bg-blue-600 text-white hover:bg-blue-700">Mulai Gratis</Button></Link>
          </div>
        </div>
      )}
    </header>
  );
}

function SectionHead({ overline, title, sub }) {
  return (
    <div className="mb-12 max-w-2xl">
      {overline && <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">{overline}</p>}
      <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">{title}</h2>
      {sub && <p className="mt-4 text-base text-slate-600 md:text-lg">{sub}</p>}
    </div>
  );
}

export default function Landing() {
  const [openFaq, setOpenFaq] = useState(0);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    document.title = "Mas Tofa AI — AI untuk Video Jualan";
    getPackages().then((d) => setPackages(d.packages || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div
          className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full opacity-[0.12] blur-3xl"
          style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 70%)" }}
        />
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 md:grid-cols-2 md:py-28">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              <Sparkles className="h-3.5 w-3.5" /> AI untuk membuat konten pemasaran lebih mudah
            </span>
            <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Bikin Video Jualan dari Foto Produk.
            </h1>
            <p className="mt-5 max-w-lg text-lg text-slate-600">
              Upload foto produk Anda, masukkan informasi produk, lalu AI membantu membuat video untuk promosi.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/daftar" data-testid="hero-cta-register">
                <Button className="group w-full rounded-full bg-blue-600 px-7 py-6 text-base text-white hover:bg-blue-700 active:scale-95 transition-transform sm:w-auto">
                  Mulai Gratis
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <Link to="/masuk" data-testid="hero-cta-login">
                <Button variant="outline" className="w-full rounded-full border-slate-300 px-7 py-6 text-base text-slate-700 hover:bg-slate-100 sm:w-auto">
                  Masuk
                </Button>
              </Link>
            </div>
          </div>

          {/* Product mockup */}
          <div className="relative">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-red-400" />
                <span className="h-3 w-3 rounded-full bg-amber-400" />
                <span className="h-3 w-3 rounded-full bg-emerald-400" />
              </div>
              <div className="flex aspect-video items-center justify-center rounded-xl bg-slate-900">
                <div className="flex flex-col items-center gap-2 text-slate-300">
                  <Film className="h-10 w-10" />
                  <span className="text-sm">Video Jualan 9:16</span>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center text-xs">
                <div className="rounded-lg bg-slate-100 py-3 font-medium text-slate-600"><Upload className="mx-auto mb-1 h-4 w-4 text-blue-600" />Upload</div>
                <div className="rounded-lg bg-slate-100 py-3 font-medium text-slate-600"><FileText className="mx-auto mb-1 h-4 w-4 text-blue-600" />Konsep</div>
                <div className="rounded-lg bg-slate-100 py-3 font-medium text-slate-600"><Film className="mx-auto mb-1 h-4 w-4 text-blue-600" />Video</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION A — Masalah */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <SectionHead
          overline="Masalah"
          title="Bikin materi promosi itu melelahkan."
          sub="Penjual online perlu menyiapkan foto, video, script, dan materi promosi lainnya. Mas Tofa AI membantu menyederhanakan proses tersebut."
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {["Foto produk", "Video promosi", "Script jualan", "Materi promosi"].map((t) => (
            <div key={t} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`problem-${t}`}>
              <p className="font-heading font-semibold text-slate-900">{t}</p>
              <p className="mt-2 text-sm text-slate-500">Perlu waktu dan usaha untuk disiapkan satu per satu.</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION B — Cara Kerja */}
      <section id="cara-kerja" className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <SectionHead overline="Cara Kerja" title="Tiga langkah sederhana." />
          <div className="grid gap-6 md:grid-cols-3">
            {STEPS.map(({ n, icon: Icon, title, desc }) => (
              <div key={n} className="relative rounded-2xl border border-slate-200 bg-slate-50 p-8" data-testid={`step-${n}`}>
                <span className="font-heading text-5xl font-bold text-slate-200">{n}</span>
                <div className="mt-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION C — Fitur */}
      <section id="fitur" className="mx-auto max-w-6xl px-5 py-20">
        <SectionHead overline="Fitur" title="Yang sudah tersedia sekarang." />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {AVAILABLE.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-xl border border-slate-200 bg-white p-6 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-sm" data-testid={`feature-${title}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-heading font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600">{desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Segera Hadir</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {COMING_SOON.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-500" data-testid={`coming-soon-${t}`}>
                {t} <span className="text-[10px] font-semibold uppercase text-amber-600">Segera</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION D — Siapa yang Cocok */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <SectionHead overline="Untuk Siapa" title="Cocok untuk Anda yang berjualan." />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCE.map(({ icon: Icon, title }) => (
              <div key={title} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid={`audience-${title}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="font-medium text-slate-900">{title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION E — Benefit */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <SectionHead overline="Manfaat" title="Kenapa pakai Mas Tofa AI?" />
        <div className="grid gap-4 sm:grid-cols-2">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-6" data-testid={`benefit-${b}`}>
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Check className="h-4 w-4" />
              </span>
              <p className="text-slate-700">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION — Harga */}
      <section id="harga" className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <SectionHead overline="Paket & Harga" title="Beli kredit sesuai kebutuhan."
            sub="Kredit adalah satuan yang digunakan untuk membuat video. 1 video = 6 kredit. Estimasi jumlah video hanya perkiraan, bukan jaminan." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="landing-pricing">
            {packages.map((pkg) => {
              const free = pkg.is_free || pkg.price <= 0;
              const highlight = pkg.code === "pro";
              return (
                <div key={pkg.code} data-testid={`landing-package-${pkg.code}`}
                  className={`flex flex-col rounded-2xl border p-6 ${highlight ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white"}`}>
                  <p className={`font-heading text-lg font-semibold ${highlight ? "text-white" : "text-slate-900"}`}>{pkg.name}</p>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className={`font-heading text-3xl font-bold ${highlight ? "text-white" : "text-slate-900"}`}>{pkg.credits}</span>
                    <span className={highlight ? "text-blue-100" : "text-slate-500"}>Credit</span>
                  </div>
                  <p className={`mt-1 text-xs ${highlight ? "text-blue-100" : "text-slate-400"}`}>≈ hingga {pkg.video_estimate} video 6 detik 720p</p>
                  <p className={`mt-4 font-heading text-2xl font-bold ${highlight ? "text-white" : "text-blue-600"}`}>
                    {free ? "Gratis" : formatRupiah(pkg.price)}
                  </p>
                  <p className={`mt-2 flex-1 text-sm ${highlight ? "text-blue-100" : "text-slate-500"}`}>{pkg.description}</p>
                  <Link to="/daftar" data-testid={`landing-package-cta-${pkg.code}`} className="mt-5">
                    <Button className={`w-full rounded-full active:scale-95 transition-transform ${highlight ? "bg-white text-blue-700 hover:bg-blue-50" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
                      {free ? "Mulai Gratis" : "Mulai Gratis"}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">Pembayaran tersedia setelah Anda masuk ke akun. Estimasi video hanya perkiraan dan bukan jumlah yang dijamin.</p>
        </div>
      </section>

      {/* SECTION F — Cara Memulai */}
      <section className="border-y border-slate-200 bg-blue-600">
        <div className="mx-auto max-w-6xl px-5 py-20 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Cara Memulai</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white md:text-4xl">Daftar → Upload Produk → Buat Video</h2>
          <Link to="/daftar" data-testid="start-cta-register" className="mt-8 inline-block">
            <Button className="rounded-full bg-white px-8 py-6 text-base font-semibold text-blue-700 hover:bg-blue-50 active:scale-95 transition-transform">
              Mulai Gratis
            </Button>
          </Link>
        </div>
      </section>

      {/* SECTION G — FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-5 py-20">
        <SectionHead overline="FAQ" title="Pertanyaan yang sering ditanya." />
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={f.q} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-slate-50"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-medium text-slate-900">{f.q}</span>
                {openFaq === i ? <Minus className="h-5 w-5 shrink-0 text-blue-600" /> : <Plus className="h-5 w-5 shrink-0 text-slate-400" />}
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5 text-sm leading-relaxed text-slate-600" data-testid={`faq-answer-${i}`}>{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* SECTION H — Final CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center md:p-16">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            Siap membuat video jualan dari produk Anda?
          </h2>
          <Link to="/daftar" data-testid="final-cta-register" className="mt-8 inline-block">
            <Button className="rounded-full bg-blue-600 px-8 py-6 text-base text-white hover:bg-blue-700 active:scale-95 transition-transform">
              Mulai Gratis <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* SECTION I — Footer */}
      <PublicFooter />
    </div>
  );
}
